import {
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { PopoverProps, PopoverTrigger } from 'src/components/PopoverTrigger';
import { CustomAvatar } from 'src/components/custom-avatar';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { SoundBoardData } from 'src/requests/soundboard';
import { SoundBoardAddEditSoundPopover } from './SoundBoardAddEditSoundPopover';
import { SoundBoardSoundItem } from './SoundBoardSoundItem';
import {
  SoundBoardButton,
  SoundsContainer,
  StyledMenu,
  StyledTextItem,
} from './styles';
import { useSoundData } from './useSoundData';

type PopoverContextProps = {
  onSelectSound: (sound: Partial<SoundBoardData>) => void;
};

export const SoundBoardPopover = ({
  target,
  handleClose,
  onSelectSound,
}: PopoverProps<PopoverContextProps>) => {
  const open = Boolean(target);
  const [searchSound, setSearchSound] = useState<string>('');
  const { translate } = useLocales();
  const soundData = useSoundData({ searchSound });

  return (
    <StyledMenu
      open={open}
      anchorEl={target}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Stack width="400px">
        <TextField
          size="small"
          value={searchSound}
          onChange={(e) => setSearchSound(e.target.value)}
          fullWidth
          placeholder={translate('communicate.sound_board.find_sound')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify
                  icon="eva:search-fill"
                  sx={{ color: 'text.disabled' }}
                />
              </InputAdornment>
            ),
          }}
        />

        {soundData.map((item, groupIndex) => (
          <SoundsContainer key={groupIndex}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              {!!item.icon ? (
                <Iconify icon={item.icon} sx={{ fontSize: 20 }} />
              ) : (
                <CustomAvatar
                  name={item.group?.name}
                  variant="circular"
                  sx={{ width: 20, height: 20, fontSize: 14 }}
                />
              )}
              <Typography variant="subtitle1">
                {item.group.name} {translate('communicate.sound_board.sounds')}
              </Typography>
            </Stack>
            <Grid container spacing={1}>
              {item.sounds.length > 0 &&
                item.sounds.map((sound) => (
                  <SoundBoardSoundItem
                    key={sound._id}
                    sound={sound}
                    onSelectSound={onSelectSound}
                  />
                ))}
              {item.default ? (
                <>
                  {!item.sounds.length && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        No sounds founded
                      </Typography>
                    </Grid>
                  )}
                </>
              ) : (
                <Grid item xs={4}>
                  <PopoverTrigger popover={SoundBoardAddEditSoundPopover}>
                    <SoundBoardButton
                      size="small"
                      variant="soft"
                      disableElevation
                    >
                      <Iconify icon="tabler:plus" />
                      <StyledTextItem variant="body2">
                        {translate('communicate.sound_board.add_sound')}
                      </StyledTextItem>
                    </SoundBoardButton>
                  </PopoverTrigger>
                </Grid>
              )}
            </Grid>
          </SoundsContainer>
        ))}
      </Stack>
    </StyledMenu>
  );
};
